import React, { useEffect, useState } from 'react';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { summarizeConversation } from 'data/api/intercom.api';
import { CARD, isForbidden } from './EventDetails';

// Every piece of this section's state carries the member it was generated for.
// Clearing on a member change inside an effect is too late — the rail
// re-renders under the newly selected family before effects run, and
// ContextRail gives these sections no key, so React reuses this instance
// across a switch and the previous family's summary would be shown once.
// Ownership is checked at read time instead, so a generation for a member who
// is no longer selected has no way to reach the screen. Same shape as
// EventDetails and useDraftReply, and it matters most here: this is prose
// about one family's grief in front of a care partner reading another's.
interface SummaryState {
  owner?: string;
  summary: string;
  loading: boolean;
  error: string;
  // Set only where trying again could actually produce a different answer.
  retryable: boolean;
}

const EMPTY: SummaryState = {
  owner: undefined,
  summary: '',
  loading: false,
  error: '',
  retryable: false,
};

// Arrowing down the queue selects a row per keypress, and each selection would
// otherwise start a getConversationByUser fan-out — the transcript read that
// degraded the whole API on 2026-08-26 — plus a Bedrock generation nobody will
// ever see. Waiting for the selection to settle means the work is never
// started rather than started and thrown away. 400ms: well past the gap
// between keypresses, and imperceptible against a generation that takes ten
// times as long. It delays an explicit Try again by the same amount, which is
// not worth a second code path.
const SETTLE_MS = 400;

// The api interceptor rejects with the backend's message string, so the HTTP
// status is gone by the time it lands here and there is no status to branch
// on. Two failures earn their own wording, both because the situation is
// ordinary rather than broken: a 403 on this family, and a conversation with
// no text in it.
const NO_HISTORY = 'NO CONVERSATION HISTORY';

const FAILED =
  'The summary could not be generated. The conversation itself is unaffected.';

// The rest read as one honest failure, but these two cannot come good however
// many times they are asked — the model is not configured for this
// environment (503, reachable mid-rollout), or the kill switch has gone off
// since the account loaded (404). A button that can never succeed teaches a
// daily user not to trust the UI, so there isn't one. A request timeout is
// the opposite case and falls through to the retryable default on purpose.
const HOPELESS = ['CONVERSATION SUMMARY IS NOT CONFIGURED', 'NOT FOUND'];

const failure = (error: unknown): { message: string; retryable: boolean } => {
  const raw = typeof error === 'string' ? error : (error as Error)?.message;
  const text = (raw || '').toUpperCase();
  if (isForbidden(error)) {
    return {
      message: 'You do not have permission to summarize this conversation.',
      retryable: false,
    };
  }
  if (text.startsWith(NO_HISTORY)) {
    return {
      message: 'There are no messages here to summarize yet.',
      retryable: false,
    };
  }
  return {
    message: FAILED,
    retryable: !HOPELESS.some((hopeless) => text.startsWith(hopeless)),
  };
};

// The rail's card, with the one mark that says a machine wrote what is inside
// it. Carried by every state so the section does not shift when a generation
// lands.
const SUMMARY_CARD = {
  ...CARD,
  borderLeft: '3px solid #356766',
  padding: '10px 12px',
};

interface IConversationSummaryProps {
  userId: string;
}

export const ConversationSummary: React.FC<IConversationSummaryProps> = ({
  userId,
}) => {
  const [state, setState] = useState<SummaryState>(EMPTY);
  const [attempt, setAttempt] = useState<number>(0);

  useEffect(() => {
    let live = true;
    const controller = new AbortController();
    setState({ ...EMPTY, owner: userId, loading: true });
    const timer = setTimeout(() => {
      summarizeConversation(userId, controller.signal)
        .then((response) => {
          if (!live) return;
          const summary = response?.summary?.trim() || '';
          // A body with nothing in it is a failure the route already refuses
          // with a 502; saying so beats an empty card that reads as a bug.
          setState(
            summary
              ? { ...EMPTY, owner: userId, summary }
              : { ...EMPTY, owner: userId, error: FAILED, retryable: true },
          );
        })
        .catch((error) => {
          if (!live) return;
          const { message, retryable } = failure(error);
          setState({ ...EMPTY, owner: userId, error: message, retryable });
        });
    }, SETTLE_MS);
    // The timer covers a selection that moves on before the request is made;
    // the abort covers one that moves on after. Between them no generation
    // outlives the selection that asked for it, on the server or on screen.
    return () => {
      live = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [userId, attempt]);

  // Anything belonging to another member reads as not-yet-generated, so there
  // is no window in which the previous family's summary is on screen.
  const owned = state.owner === userId ? state : null;
  const loading = !owned || owned.loading;

  if (owned?.error) {
    return (
      <Stack sx={{ ...SUMMARY_CARD, gap: '2px', alignItems: 'flex-start' }}>
        <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
          {owned.error}
        </Typography>
        {owned.retryable && (
          <Button
            size="small"
            onClick={() => setAttempt((current) => current + 1)}
            sx={{
              color: '#356766',
              textTransform: 'none',
              fontWeight: 600,
              paddingX: 0,
              minWidth: 0,
            }}
          >
            Try again
          </Button>
        )}
      </Stack>
    );
  }

  if (loading) {
    return (
      <Stack sx={SUMMARY_CARD} aria-busy="true">
        {/* Four lines at the prose's own size, so the section holds roughly
            the height it settles at and the rail does not jump when a
            3-to-4-second generation lands. */}
        {['100%', '100%', '84%', '46%'].map((width, index) => (
          <Skeleton key={index} width={width} sx={{ fontSize: '14px' }} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack sx={{ ...SUMMARY_CARD, gap: '8px' }}>
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: '22px',
          color: '#213B54',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {owned?.summary}
      </Typography>
      <Typography
        sx={{
          borderTop: '1px solid #F4F2F0',
          paddingTop: '8px',
          fontSize: '13px',
          lineHeight: '18px',
          color: '#9A9A9A',
        }}
      >
        Written by AI from this conversation. Read the thread before you rely on
        it.
      </Typography>
    </Stack>
  );
};
